import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import { Link, useParams } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { useDispatch, useSelector } from "react-redux";
import { addCart } from "../redux/action";
import { openChatWithStore } from "../redux/action/chatSlice";
import toast from "react-hot-toast";

import { formatRupiah } from "../utils/currency";
import { categoryLabel } from "../utils/categories";
import { mapProduct } from "../utils/productMapping";
import { Footer, Navbar } from "../components";
import api from "../utils/api";

const Product = () => {
  const { id } = useParams();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(true);
  const [isFavorited, setIsFavorited] = useState(false);

  // Tabs state
  const [activeTab, setActiveTab] = useState('deskripsi');
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);

  const [discussions, setDiscussions] = useState([]);
  const [loadingDiscussions, setLoadingDiscussions] = useState(false);
  const [newDiscussion, setNewDiscussion] = useState('');
  const [submittingDiscussion, setSubmittingDiscussion] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');

  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);

  const addProduct = (p) => {
    dispatch(addCart(p));
    toast.success("Added to cart");
  };

  useEffect(() => {
    const controller = new AbortController();

    const getProduct = async () => {
      try {
        setLoading(true);
        setLoadingSimilar(true);

        const res = await fetch(`${process.env.REACT_APP_API_URL || "https://apistore.iqbaldeveloper.my.id/api"}/products/${id}`, {
          signal: controller.signal,
        });
        const data = await res.json();
        const mappedProduct = mapProduct(data);
        setProduct(mappedProduct);
        setLoading(false);

        // Fetch similar products natively using local API, falling back to basic list
        const res2 = await fetch(`${process.env.REACT_APP_API_URL || "https://apistore.iqbaldeveloper.my.id/api"}/products`, {
          signal: controller.signal 
        });
        const data2 = await res2.json();
        
        const productsData = data2.data || data2;

        // Optional: remove the same product from similar list
        const mappedSimilar = (productsData || []).map(mapProduct).filter((x) => String(x.id) !== String(id));
        setSimilarProducts(mappedSimilar);
      } catch (err) {
        if (err?.name !== "AbortError") {
          console.error(err);
          toast.error("Gagal mengambil detail produk");
        }
      } finally {
        setLoading(false);
        setLoadingSimilar(false);
      }
    };

    getProduct();
    
    if (isAuthenticated) {
      api.get(`/api/wishlists/${id}/check`)
        .then(res => setIsFavorited(res.data.in_wishlist))
        .catch(err => console.log(err));
    }

    // Fetch Reviews
    setLoadingReviews(true);
    api.get(`/api/reviews?product_id=${id}&status=approved`)
      .then(res => {
        setReviews(res.data.data || []);
      })
      .catch(err => console.log(err))
      .finally(() => setLoadingReviews(false));

    // Fetch Discussions
    setLoadingDiscussions(true);
    api.get(`/api/products/${id}/discussions`)
      .then(res => {
        setDiscussions(res.data.data || []);
      })
      .catch(err => console.log(err))
      .finally(() => setLoadingDiscussions(false));

    return () => controller.abort();
  }, [id, isAuthenticated]);

  const toggleWishlist = async () => {
    if (!isAuthenticated) return toast.error("Silakan login untuk menyimpan wishlist");
    try {
      if (isFavorited) {
        await api.delete(`/api/wishlists/${id}`);
        setIsFavorited(false);
        toast.success("Dihapus dari wishlist");
      } else {
        await api.post('/api/wishlists', { product_id: id });
        setIsFavorited(true);
        toast.success("Ditambahkan ke wishlist");
      }
    } catch (err) {
      toast.error("Gagal mengupdate wishlist");
    }
  };

  const RatingStars = ({ value = 0 }) => {
    const v = Math.max(0, Math.min(5, Number(value) || 0));
    const full = Math.floor(v);
    const half = v - full >= 0.5;

    return (
      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }).map((_, i) => {
          const idx = i + 1;
          const filled = idx <= full;
          const halfFill = idx === full + 1 && half;

          return (
            <span
              key={i}
              className={[
                "inline-block h-4 w-4 rounded-sm",
                filled
                  ? "bg-amber-400"
                  : halfFill
                  ? "bg-gradient-to-r from-amber-400 to-white/20"
                  : "bg-white/15",
              ].join(" ")}
              aria-hidden="true"
            />
          );
        })}
        <span className="ml-2 text-sm text-zinc-300">
          {v.toFixed(1)}
        </span>
      </div>
    );
  };

  const LoadingDetail = () => (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <Skeleton height={420} borderRadius={16} />
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
          <Skeleton height={16} width={160} />
          <div className="mt-3">
            <Skeleton height={34} />
          </div>
          <div className="mt-3">
            <Skeleton height={18} width={220} />
          </div>
          <div className="mt-4">
            <Skeleton height={28} width={120} />
          </div>
          <div className="mt-4">
            <Skeleton height={14} count={4} />
          </div>
          <div className="mt-6 grid grid-cols-2 gap-2">
            <Skeleton height={44} borderRadius={12} />
            <Skeleton height={44} borderRadius={12} />
          </div>
        </div>
      </div>
    </div>
  );

  const LoadingSimilar = () => (
    <div className="flex gap-4 py-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="w-[240px] shrink-0 rounded-2xl border border-white/10 bg-white/5 p-4"
        >
          <Skeleton height={180} borderRadius={16} />
          <div className="mt-3 space-y-2">
            <Skeleton height={16} width="85%" />
            <Skeleton height={18} width="40%" />
            <Skeleton height={40} borderRadius={12} />
          </div>
        </div>
      ))}
    </div>
  );

  const handlePostDiscussion = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) return toast.error("Silakan login untuk bertanya");
    if (!newDiscussion.trim()) return;

    setSubmittingDiscussion(true);
    try {
      const res = await api.post(`/api/products/${id}/discussions`, { message: newDiscussion });
      toast.success("Pertanyaan terkirim!");
      setNewDiscussion('');
      setDiscussions([res.data.data, ...discussions]);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Gagal mengirim pertanyaan");
    } finally {
      setSubmittingDiscussion(false);
    }
  };

  const handlePostReply = async (e, parentId) => {
    e.preventDefault();
    if (!isAuthenticated) return toast.error("Silakan login untuk membalas");
    if (!replyMessage.trim()) return;
    
    setSubmittingDiscussion(true);
    try {
      const res = await api.post(`/api/products/${id}/discussions`, { 
        message: replyMessage,
        parent_id: parentId 
      });
      toast.success("Balasan terkirim!");
      setReplyMessage('');
      setReplyingTo(null);
      // Update discussions list locally
      const updatedDiscussions = discussions.map(d => {
        if (d.id === parentId) {
           return { ...d, replies: [...(d.replies || []), res.data.data] };
        }
        return d;
      });
      setDiscussions(updatedDiscussions);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Gagal mengirim balasan");
    } finally {
      setSubmittingDiscussion(false);
    }
  };

  return (
    <>
      <Navbar />

      <main className="bg-zinc-950 text-white">
        {/* Detail */}
        {loading || !product ? (
          <LoadingDetail />
        ) : (
          <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Image */}
              <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/5">
                <div className="aspect-square w-full p-6">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="h-full w-full object-contain"
                    loading="lazy"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-white/5 px-3 py-1 text-xs font-semibold text-zinc-300 ring-1 ring-white/10">
                    {categoryLabel(product.category)}
                  </span>
                  {product?.rating?.count != null && (
                    <span className="rounded-full bg-white/5 px-3 py-1 text-xs font-semibold text-zinc-300 ring-1 ring-white/10">
                      {product.rating.count} reviews
                    </span>
                  )}
                </div>

                <h1 className="mt-3 text-2xl font-extrabold tracking-tight sm:text-3xl">
                  {product.title}
                </h1>

                <div className="mt-3">
                  <RatingStars value={product?.rating?.rate || 0} />
                </div>

                <div className="mt-4 flex items-end justify-between gap-3">
                  <div className="flex flex-col gap-1">
                     {product.active_flash_sale ? (
                        <>
                           <span className="text-sm text-zinc-500 line-through decoration-rose-500/50">
                             {formatRupiah(product.price)}
                           </span>
                           <div className="flex items-center gap-2">
                             <div className="text-3xl font-extrabold text-rose-500">
                               {formatRupiah(product.final_price)}
                             </div>
                             <span className="rounded-full bg-rose-500/15 px-2 py-1 text-xs font-extrabold text-rose-300 ring-1 ring-rose-500/20">
                               Flash Sale
                             </span>
                           </div>
                        </>
                     ) : (
                        <div className="text-3xl font-extrabold">
                          {formatRupiah(product.final_price || product.price)}
                        </div>
                     )}
                  </div>
                  {product.product_type === 'digital' && (
                    <div className="text-sm font-semibold text-indigo-400 bg-indigo-500/10 px-3 py-1.5 rounded-full ring-1 ring-indigo-500/20">
                      Instant download
                    </div>
                  )}
                </div>

                <div className="mt-8 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <button
                    onClick={() => addProduct(product)}
                    className="inline-flex items-center justify-center rounded-xl bg-emerald-500 px-4 py-3 text-sm font-extrabold text-white transition hover:bg-emerald-400"
                  >
                    Add to Cart
                  </button>

                  <div className="flex gap-2">
                    <Link
                      to="/cart"
                      className="flex-1 inline-flex items-center justify-center rounded-xl bg-white px-4 py-3 text-sm font-extrabold text-black transition hover:bg-zinc-100"
                    >
                      Go to Cart
                    </Link>
                    <button
                      onClick={toggleWishlist}
                      title={isFavorited ? "Hapus dari Wishlist" : "Simpan ke Wishlist"}
                      className="inline-flex flex-shrink-0 aspect-square items-center justify-center rounded-xl bg-white/5 border border-white/10 text-rose-500 hover:bg-rose-500 hover:text-white transition group"
                    >
                      <svg className="w-5 h-5" fill={isFavorited ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </button>
                  </div>
                </div>

                {product.store && (
                  <button
                    onClick={() => dispatch(openChatWithStore(product.store))}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-500/10 border border-indigo-500/20 px-4 py-3 text-sm font-bold text-indigo-400 transition hover:bg-indigo-500 hover:text-white"
                  >
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    Chat Penjual
                  </button>
                )}
              </div>
            </div>

            {/* Product Tabs (Deskripsi, Ulasan, Diskusi) */}
            <div className="mt-10 rounded-2xl border border-white/10 bg-zinc-900/50 p-6 sm:p-8 overflow-hidden shadow-xl">
               <div className="flex gap-6 border-b border-white/10 mb-6 overflow-x-auto scrollbar-hide">
                 {['deskripsi', 'ulasan', 'diskusi'].map((tab) => (
                   <button
                     key={tab}
                     onClick={() => setActiveTab(tab)}
                     className={`pb-3 text-sm font-extrabold transition-all border-b-2 whitespace-nowrap ${activeTab === tab ? 'border-emerald-500 text-emerald-400' : 'border-transparent text-zinc-400 hover:text-white'}`}
                   >
                     {tab.charAt(0).toUpperCase() + tab.slice(1)}
                     {tab === 'ulasan' && ` (${reviews.length})`}
                     {tab === 'diskusi' && ` (${discussions.length})`}
                   </button>
                 ))}
               </div>

               {/* Tab Content */}
               <div className="min-h-[200px]">
                 {activeTab === 'deskripsi' && (
                   <div className="prose prose-invert max-w-none text-sm leading-relaxed text-zinc-300">
                     <p className="whitespace-pre-wrap">{product.description}</p>
                   </div>
                 )}

                 {activeTab === 'ulasan' && (
                   <div>
                     {loadingReviews ? (
                       <div className="flex justify-center py-10"><div className="w-8 h-8 animate-spin rounded-full border-4 border-zinc-800 border-t-emerald-500"></div></div>
                     ) : reviews.length === 0 ? (
                       <div className="text-center py-10 bg-white/5 rounded-2xl border border-white/10">
                         <p className="text-zinc-400 text-sm">Belum ada ulasan untuk produk ini.</p>
                       </div>
                     ) : (
                       <div className="space-y-6">
                         {reviews.map((review) => (
                           <div key={review.id} className="border-b border-white/10 pb-6 last:border-0">
                             <div className="flex items-center gap-3 mb-2">
                               <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center font-bold text-emerald-500 uppercase shrink-0">
                                 {review.user?.name?.charAt(0) || 'U'}
                               </div>
                               <div>
                                 <p className="text-sm font-bold text-white">{review.user?.name || 'Anonim'}</p>
                                 <div className="flex items-center gap-2">
                                   <RatingStars value={review.rating} />
                                   <span className="text-xs text-zinc-500">{new Date(review.created_at).toLocaleDateString('id-ID')}</span>
                                 </div>
                               </div>
                             </div>
                             {review.comment && (
                               <p className="text-sm text-zinc-300 mt-3 whitespace-pre-wrap ml-13">{review.comment}</p>
                             )}
                           </div>
                         ))}
                       </div>
                     )}
                   </div>
                 )}

                 {activeTab === 'diskusi' && (
                   <div>
                     {/* Input form */}
                     <form onSubmit={handlePostDiscussion} className="mb-8 p-4 rounded-2xl bg-white/5 border border-white/10">
                       <h4 className="text-sm font-bold text-white mb-3">Tanyakan sesuatu tentang produk ini</h4>
                       <textarea
                         value={newDiscussion}
                         onChange={(e) => setNewDiscussion(e.target.value)}
                         placeholder={isAuthenticated ? "Ketik pertanyaan Anda..." : "Login terlebih dahulu untuk bertanya"}
                         disabled={!isAuthenticated}
                         className="w-full bg-zinc-900 border border-white/10 rounded-xl p-3 text-sm text-white focus:ring-1 focus:ring-emerald-500 outline-none resize-none mb-3"
                         rows="2"
                       />
                       <div className="flex justify-end">
                         <button
                           type="submit"
                           disabled={submittingDiscussion || !newDiscussion.trim() || !isAuthenticated}
                           className="px-5 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-bold transition-all disabled:opacity-50"
                         >
                           {submittingDiscussion ? "Mengirim..." : "Kirim"}
                         </button>
                       </div>
                     </form>

                     {loadingDiscussions ? (
                       <div className="flex justify-center py-10"><div className="w-8 h-8 animate-spin rounded-full border-4 border-zinc-800 border-t-emerald-500"></div></div>
                     ) : discussions.length === 0 ? (
                       <div className="text-center py-10">
                         <p className="text-zinc-500 text-sm">Belum ada diskusi. Jadilah yang pertama bertanya!</p>
                       </div>
                     ) : (
                       <div className="space-y-6">
                         {discussions.map((discussion) => (
                           <div key={discussion.id} className="border-b border-white/10 pb-6 last:border-0 last:pb-0">
                             <div className="flex gap-4 items-start">
                               <div className="w-10 h-10 rounded-full bg-zinc-800 shrink-0 flex items-center justify-center font-bold text-white uppercase text-sm">
                                 {discussion.user?.name?.charAt(0) || 'U'}
                               </div>
                               <div className="flex-1">
                                 <div className="flex items-center gap-2 mb-1">
                                    <span className="font-bold text-sm text-white">{discussion.user?.name || 'Anonim'}</span>
                                    <span className="text-xs text-zinc-500">{new Date(discussion.created_at).toLocaleDateString('id-ID')}</span>
                                 </div>
                                 <p className="text-sm text-zinc-300 whitespace-pre-wrap">{discussion.message}</p>
                                 
                                 {/* Replies */}
                                 {discussion.replies && discussion.replies.length > 0 && (
                                   <div className="mt-4 space-y-4 pl-4 border-l-2 border-zinc-800">
                                     {discussion.replies.map((reply) => (
                                        <div key={reply.id} className="flex gap-3">
                                          <div className="w-8 h-8 rounded-full bg-indigo-500/20 shrink-0 flex items-center justify-center font-bold text-indigo-400 uppercase text-xs">
                                            {reply.user?.name?.charAt(0) || 'S'}
                                          </div>
                                          <div>
                                            <div className="flex items-center gap-2 mb-1">
                                              <span className="font-bold text-xs text-indigo-400">{reply.user?.name || 'Seller'}</span>
                                              {reply.user?.role === 'seller' && <span className="bg-emerald-500/20 text-emerald-400 text-[10px] px-1.5 py-0.5 rounded font-bold">Seller</span>}
                                              <span className="text-xs text-zinc-500">{new Date(reply.created_at).toLocaleDateString('id-ID')}</span>
                                            </div>
                                            <p className="text-sm text-zinc-300 whitespace-pre-wrap">{reply.message}</p>
                                          </div>
                                        </div>
                                     ))}
                                   </div>
                                 )}

                                 <button onClick={() => setReplyingTo(replyingTo === discussion.id ? null : discussion.id)} className="mt-3 text-xs font-bold text-emerald-500 hover:text-emerald-400 transition">
                                   Balas
                                 </button>

                                 {replyingTo === discussion.id && (
                                   <form onSubmit={(e) => handlePostReply(e, discussion.id)} className="mt-3 ml-4">
                                     <textarea
                                       value={replyMessage}
                                       onChange={(e) => setReplyMessage(e.target.value)}
                                       placeholder="Tulis balasan..."
                                       className="w-full bg-zinc-900 border border-white/10 rounded-xl p-3 text-sm text-white focus:ring-1 focus:ring-emerald-500 outline-none resize-none mb-2"
                                       rows="2"
                                     />
                                     <div className="flex justify-end gap-2">
                                       <button type="button" onClick={() => setReplyingTo(null)} className="px-3 py-1.5 text-xs text-zinc-400 hover:text-white transition">Batal</button>
                                       <button type="submit" disabled={!replyMessage.trim()} className="px-4 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-400 disabled:opacity-50">Kirim Balasan</button>
                                     </div>
                                   </form>
                                 )}
                               </div>
                             </div>
                           </div>
                         ))}
                       </div>
                     )}
                   </div>
                 )}
               </div>
            </div>
          </section>
        )}

        {/* Similar */}
        <section className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-3">
            <h2 className="text-lg font-extrabold sm:text-xl">You may also like</h2>
            <Link to="/product" className="text-sm font-semibold text-zinc-300 hover:text-white">
              Lihat semua
            </Link>
          </div>

          <div className="mt-4 hidden md:block rounded-2xl border border-white/10 bg-white/5 p-3">
            <Marquee pauseOnHover pauseOnClick speed={50} gradient={false}>
              {loadingSimilar ? (
                <LoadingSimilar />
              ) : (
                <div className="flex gap-4 py-2">
                  {similarProducts.map((item) => (
                    <article
                      key={item.id}
                      className="w-[260px] shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/40 transition hover:bg-white/10"
                    >
                      <Link to={`/product/${item.id}`} className="block">
                        <div className="aspect-[4/3] w-full bg-white/5 p-4">
                          <img
                            src={item.image}
                            alt={item.title}
                            className="h-full w-full object-contain"
                            loading="lazy"
                          />
                        </div>
                      </Link>

                      <div className="p-4">
                        <h3 className="line-clamp-1 text-sm font-extrabold">
                          {item.title}
                        </h3>

                        <div className="mt-2 flex items-center justify-between gap-2">
                          <div className="flex flex-col shrink-0 gap-0.5">
                             {item.active_flash_sale ? (
                                <>
                                  <span className="text-[10px] text-zinc-500 line-through decoration-rose-500/50">
                                    {formatRupiah(item.price)}
                                  </span>
                                  <span className="text-sm font-extrabold text-rose-400">
                                    {formatRupiah(item.final_price)}
                                  </span>
                                </>
                             ) : (
                                <span className="text-sm font-extrabold text-emerald-300">
                                  {formatRupiah(item.final_price || item.price)}
                                </span>
                             )}
                          </div>
                          <span className="rounded-full bg-white/5 px-2 py-1 text-[11px] font-semibold text-zinc-300 ring-1 ring-white/10 shrink-0">
                            {categoryLabel(item.category)}
                          </span>
                        </div>

                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <Link
                            to={`/product/${item.id}`}
                            className="inline-flex items-center justify-center rounded-xl bg-white px-3 py-2 text-sm font-extrabold text-black transition hover:bg-zinc-100"
                          >
                            Buy
                          </Link>
                          <button
                            onClick={() => addProduct(item)}
                            className="inline-flex items-center justify-center rounded-xl bg-emerald-500 px-3 py-2 text-sm font-extrabold text-white transition hover:bg-emerald-400"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </Marquee>
          </div>

          {/* Mobile fallback (no marquee) */}
          <div className="mt-4 md:hidden">
            {loadingSimilar ? (
              <LoadingSimilar />
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {similarProducts.slice(0, 6).map((item) => (
                  <article
                    key={item.id}
                    className="overflow-hidden rounded-2xl border border-white/10 bg-white/5 transition hover:bg-white/10"
                  >
                    <Link to={`/product/${item.id}`} className="block">
                      <div className="aspect-[4/3] w-full bg-white/5 p-4">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="h-full w-full object-contain"
                          loading="lazy"
                        />
                      </div>
                    </Link>

                    <div className="p-4">
                      <h3 className="line-clamp-1 text-sm font-extrabold">
                        {item.title}
                      </h3>
                      <div className="mt-2 flex items-center justify-between">
                          <div className="flex flex-col shrink-0 gap-0.5">
                             {item.active_flash_sale ? (
                                <>
                                  <span className="text-[10px] text-zinc-500 line-through decoration-rose-500/50">
                                    {formatRupiah(item.price)}
                                  </span>
                                  <span className="text-sm font-extrabold text-rose-400">
                                    {formatRupiah(item.final_price)}
                                  </span>
                                </>
                             ) : (
                                <span className="text-sm font-extrabold text-emerald-300">
                                  {formatRupiah(item.final_price || item.price)}
                                </span>
                             )}
                          </div>
                        <div className="text-xs text-zinc-400">
                          {categoryLabel(item.category)}
                        </div>
                        <button
                          onClick={() => addProduct(item)}
                          className="rounded-xl bg-emerald-500 px-3 py-2 text-sm font-extrabold text-white hover:bg-emerald-400"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Product;
